import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../api';

export default function BannerCarousel({ onBannersLoaded }) {
    const [banners, setBanners] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadBanners();
    }, []);

    useEffect(() => {
        if (banners.length > 1) {
            const timer = setInterval(() => {
                setCurrentIndex((prev) => (prev + 1) % banners.length);
            }, 5000); // Auto-advance every 5 seconds

            return () => clearInterval(timer);
        }
    }, [banners.length]);

    const loadBanners = async () => {
        try {
            const data = await api.getBanners();
            setBanners(data);
            // Notify parent component about banner availability
            if (onBannersLoaded) {
                onBannersLoaded(data.length > 0);
            }
        } catch (error) {
            console.error('Error loading banners:', error);
            if (onBannersLoaded) {
                onBannersLoaded(false);
            }
        } finally {
            setLoading(false);
        }
    };

    const goToPrevious = () => {
        setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
    };

    const goToNext = () => {
        setCurrentIndex((prev) => (prev + 1) % banners.length);
    };

    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    if (loading) {
        return (
            <div className="skeleton" style={{
                height: '400px',
                borderRadius: '16px',
                margin: '24px 0'
            }} />
        );
    }

    if (banners.length === 0) {
        return null; // Don't show anything if no active banners
    }

    const currentBanner = banners[currentIndex];

    return (
        <div style={{
            position: 'relative',
            width: '100%',
            maxWidth: '100%',
            margin: '0',
            borderRadius: '0',
            overflow: 'hidden',
            boxShadow: 'none'
        }}>
            {/* Banner Container */}
            <div style={{
                position: 'relative',
                paddingBottom: '31.25%',
                background: '#f0f0f0',
                minHeight: window.innerWidth <= 768 ? '500px' : 'auto'
            }}>
                {/* Desktop Image */}
                <picture>
                    <source
                        media="(max-width: 768px)"
                        srcSet={currentBanner.mobile_image_url || currentBanner.desktop_image_url}
                    />
                    <img
                        src={currentBanner.desktop_image_url}
                        alt={currentBanner.title}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            cursor: currentBanner.link ? 'pointer' : 'default'
                        }}
                        onClick={() => {
                            if (currentBanner.link) {
                                window.open(currentBanner.link, '_blank', 'noopener,noreferrer');
                            }
                        }}
                    />
                </picture>

                {/* Text Overlay - HIDDEN */}
                {currentBanner.title && (
                    <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: '24px',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                        color: 'white',
                        display: 'none'  // Hidden as requested
                    }}>
                        <h2 style={{
                            fontSize: '28px',
                            fontWeight: '700',
                            margin: 0,
                            textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                        }}>
                            {currentBanner.title}
                        </h2>
                    </div>
                )}
            </div>

            {/* Navigation Arrows (only show if multiple banners) */}
            {banners.length > 1 && (
                <>
                    <button
                        onClick={goToPrevious}
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '16px',
                            transform: 'translateY(-50%)',
                            background: 'rgba(255,255,255,0.9)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '48px',
                            height: '48px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
                            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                        }}
                    >
                        <ChevronLeft size={24} color="#333" />
                    </button>

                    <button
                        onClick={goToNext}
                        style={{
                            position: 'absolute',
                            top: '50%',
                            right: '16px',
                            transform: 'translateY(-50%)',
                            background: 'rgba(255,255,255,0.9)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '48px',
                            height: '48px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
                            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                        }}
                    >
                        <ChevronRight size={24} color="#333" />
                    </button>

                    {/* Dots Indicator */}
                    <div style={{
                        position: 'absolute',
                        bottom: '16px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: '8px'
                    }}>
                        {banners.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => goToSlide(index)}
                                style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    border: 'none',
                                    background: index === currentIndex ? 'white' : 'rgba(255,255,255,0.5)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                                }}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
